/**
 * 최종 제출 요청과 결과 확인. 디자인 v0.3 상태표 7-2 ~ 7-10.
 *
 * - 제출 의도 하나에 요청 키 하나. 응답을 못 받은 재확인·같은 학생 재로그인 뒤 확인에는 같은 키·같은 내용.
 *   확정 거부(422 submission_incomplete)를 받은 뒤 다시 제출할 때만 새 키.
 * - 4초는 화면 대기 기준이다. 4초가 지나도 요청을 끊지 않는다. 진행 중 제출 요청은 하나만.
 * - 응답 없음·네트워크 오류(그리고 5xx)로 끝났을 때만 같은 키로 다시 보낸다: 1.5초 → 3초 → 6초 → 8초(이후 8초).
 * - 상태 조회는 2초마다 한 번, 동시에 하나만. 조회 결과 in-progress로는 아무것도 확정하지 않는다.
 * - 확인 창(30초)은 요청·조회와 무관한 타이머다. 조회가 오래 걸려도 30초가 되면 7-8로 바꾼다.
 *   7-8에서는 자동 재전송·자동 조회를 멈추고 잠금을 유지한다. 진행 중이던 요청의 응답은 그 뒤에 와도 처리한다.
 * - 확인한 현재 상태(submitted·void)는 늦게 온 과거 결과(422)로 되돌리지 않는다(7-10).
 * - (v0.2, 디자인 v0.4.1·총괄 DEV-I-002) 다시 보낸 요청의 422는 기억만 하고 확인 진행을 멈추지 않는다.
 *   그 422를 받은 뒤 시작한 조회가 in-progress면 거부 확정, submitted·void면 그 상태. 422 이전에 시작한 조회의
 *   in-progress로는 거부를 확정하지 않는다. 422를 받은 뒤에는 같은 제출을 다시 보내지 않는다.
 * - 조회 응답은 이 시도(attemptId)·이 세대의 것일 때만, 그리고 이 확인이 폐기되지 않았을 때만 반영한다.
 * - (v0.3, 총괄 DEV-I-003) 지연 0으로 바로 조회하는 것은 "422 이전에 시작한 조회가 성공으로 끝나 새 조회가 필요한 경우"뿐이다.
 *   조회 실패 뒤 재시도는 언제나 설정된 조회 간격(pollMs)을 지킨다.
 */
import { devlog } from './devlog.js';
import { currentGen, type ApiResult, type Transport } from './transport.js';
import type { AttemptStatus } from './types.js';

export type SubmitPhase =
  | 'idle'
  | 'submitting'      // 7-2 (4초 전)
  | 'checking'        // 7-4
  | 'stalled'         // 7-8
  | 'relogin'         // 7-9
  | 'rejected'        // 7-3
  | 'done'            // 7-5
  | 'done-confirmed'  // 7-5b
  | 'already'         // 7-6
  | 'void'            // 7-7
  | 'forbidden';

interface StatusResult { readonly status: AttemptStatus; readonly seq: number }

export const FINAL_PHASES: readonly SubmitPhase[] = ['done', 'done-confirmed', 'already', 'void'];
/** 입력이 잠기는 단계(S-7·S-6) */
export const LOCKING_PHASES: readonly SubmitPhase[] = ['submitting', 'checking', 'stalled', 'relogin', 'done', 'done-confirmed', 'already', 'void'];

export interface Rejection {
  readonly unansweredItemIds: readonly string[];
  readonly inputErrorItemIds: readonly string[];
  readonly unselectedGrPointIds: readonly string[];
}

export interface SubmitTiming {
  readonly displayWaitMs: number;
  readonly resendDelaysMs: readonly number[];
  readonly pollMs: number;
  readonly windowMs: number;
}
export const DEFAULT_TIMING: SubmitTiming = { displayWaitMs: 4000, resendDelaysMs: [1500, 3000, 6000, 8000], pollMs: 2000, windowMs: 30000 };

export type SubmitEvent =
  | { readonly type: 'phase'; readonly phase: SubmitPhase }
  | { readonly type: 'unauthorized' }
  | { readonly type: 'forbidden'; readonly code: string };

export interface SubmitDeps {
  readonly transport: Transport;
  readonly attemptId: string;
  readonly newKey: () => string;
  readonly emit: (e: SubmitEvent) => void;
  readonly timing?: SubmitTiming;
}

export class SubmitController {
  phase: SubmitPhase = 'idle';
  key: string | null = null;
  rejection: Rejection | null = null;
  /** 조회로 확인한 현재 상태 */
  knownStatus: AttemptStatus | null = null;
  sendCount = 0;
  pollCount = 0;
  /** 실패로 끝난 조회 수(개발 확인용) */
  pollFailCount = 0;

  private inflight = false;
  private pollInflight = false;
  private statusPromise: Promise<StatusResult | null> | null = null;
  /** 시작한 조회 순번 */
  private statusSeq = 0;
  /** 다시 보낸 요청에서 받은 422(확정 전). afterSeq보다 뒤에 시작한 조회로만 확정한다. */
  private pending422: { readonly body: Record<string, any>; readonly afterSeq: number } | null = null;
  private resendIdx = 0;
  private displayTimer: ReturnType<typeof setTimeout> | null = null;
  private windowTimer: ReturnType<typeof setTimeout> | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private resendTimer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  private readonly t: SubmitTiming;

  constructor(private readonly d: SubmitDeps) {
    this.t = d.timing ?? DEFAULT_TIMING;
  }

  isFinal(): boolean { return FINAL_PHASES.includes(this.phase); }
  locksInput(): boolean { return LOCKING_PHASES.includes(this.phase); }
  /** 결과가 확정되지 않은 제출이 있는지(나가기·재로그인 안내용) */
  uncertain(): boolean { return this.phase === 'submitting' || this.phase === 'checking' || this.phase === 'stalled' || this.phase === 'relogin'; }
  hasInflight(): boolean { return this.inflight; }

  /** 학생이 "제출하기"를 누름(7-1 확인을 통과한 뒤) */
  start(): void {
    if (this.phase !== 'idle' && this.phase !== 'rejected') return;
    this.key ??= this.d.newKey();
    this.rejection = null;
    this.pending422 = null;
    this.sendCount = 0;
    this.resendIdx = 0;
    this.setPhase('submitting');
    void this.send();
    this.displayTimer = setTimeout(() => { this.displayTimer = null; if (this.phase === 'submitting') this.enterChecking(); }, this.t.displayWaitMs);
  }

  /** 7-3 거부 화면을 닫고 풀이로 */
  dismissRejection(): void {
    if (this.phase === 'rejected') this.setPhase('idle');
  }

  /** 7-8 "다시 확인": 30초 창을 새로 연다. 진행 중 요청이 있으면 새로 보내지 않고 조회만 다시 시작한다. */
  recheck(): void {
    if (this.phase !== 'stalled') return;
    this.resendIdx = 0;
    this.setPhase('checking');
    this.openWindow();
    if (!this.inflight && this.pending422 === null) void this.send();
    this.schedulePoll(0);
  }

  /** 7-9 → 같은 학생 재로그인 뒤 R-4: 같은 키로 먼저 확인 */
  afterRelogin(): void {
    if (this.phase !== 'relogin') return;
    this.resendIdx = 0;
    this.setPhase('checking');
    this.openWindow();
    if (!this.inflight && this.pending422 === null) void this.send();
    this.schedulePoll(0);
  }

  dispose(): void {
    this.disposed = true;
    this.clearTimers();
  }

  // ---------------------------------------------------------------- 내부

  private async send(): Promise<void> {
    if (this.inflight || this.key === null) return;
    const key = this.key;
    this.inflight = true;
    this.sendCount++;
    const r = await this.d.transport.request('POST', `/attempts/${this.d.attemptId}/submit`, { key, body: {} });
    this.inflight = false;
    if (this.disposed || r.gen !== currentGen()) return;
    this.handleSubmit(r, key);
  }

  private handleSubmit(r: ApiResult, key: string): void {
    if (this.isFinal()) return; // 확인한 현재 상태를 되돌리지 않는다
    if (r.kind === 'network' || r.status >= 500) {
      if (r.kind === 'http') devlog('submit', `http_${r.status}:${String(r.body['error'] ?? '')}`, key);
      this.enterChecking();
      this.scheduleResend();
      return;
    }
    const code = String(r.body['error'] ?? '');
    if (r.status === 200 && r.body['submitted'] === true) { this.finish('done'); return; }
    if (r.status === 422 && code === 'submission_incomplete') { void this.onRejected(r.body); return; }
    if (r.status === 409 && code === 'already_submitted') { this.finish('already'); return; }
    if (r.status === 409 && code === 'attempt_void') { this.finish('void'); return; }
    if (r.status === 401) { this.toRelogin(); return; }
    if (r.status === 403) { this.clearTimers(); this.setPhase('forbidden'); this.d.emit({ type: 'forbidden', code }); return; }
    // request_in_unknown_state·request_key_reused·그 밖: 결과 불명확. 새 키를 만들지 않고 확인을 계속한다.
    devlog('submit', code === '' ? `http_${r.status}` : code, key);
    this.enterChecking();
  }

  /** 7-10: 422가 와도 이미 확인한 현재 상태를 따른다. 다시 보낸 요청의 422면 기억만 하고, 그 뒤 시작한 조회로 확정한다. */
  private onRejected(body: Record<string, any>): void {
    if (this.knownStatus === 'submitted') { this.finish('done-confirmed'); return; }
    if (this.knownStatus === 'void') { this.finish('void'); return; }
    if (this.sendCount > 1) {
      this.pending422 = { body, afterSeq: this.statusSeq };
      if (this.resendTimer !== null) { clearTimeout(this.resendTimer); this.resendTimer = null; }
      this.enterChecking(); // 이미 확인 중이면 그대로. 30초 창은 독립 타이머
      if (this.phase === 'checking' && !this.pollInflight) this.schedulePoll(0);
      return;
    }
    this.reject(body);
  }

  private reject(body: Record<string, any>): void {
    this.clearTimers();
    this.pending422 = null;
    this.rejection = {
      unansweredItemIds: body['unansweredItemIds'] ?? [],
      inputErrorItemIds: body['inputErrorItemIds'] ?? [],
      unselectedGrPointIds: body['unselectedGrPointIds'] ?? [],
    };
    this.key = null; // 다음 제출은 새 키
    this.setPhase('rejected');
  }

  /** 조회 결과 반영(확인 중·7-8 모두). 폐기·세대·시도 확인은 fetchStatus에서 끝냈다. */
  private applyStatus(r: StatusResult): void {
    if (this.isFinal() || this.phase === 'rejected' || this.phase === 'idle') return;
    if (r.status === 'submitted') { this.finish('done-confirmed'); return; }
    if (r.status === 'void') { this.finish('void'); return; }
    if (r.status === 'in-progress' && this.pending422 !== null && r.seq > this.pending422.afterSeq) this.reject(this.pending422.body);
  }

  private enterChecking(): void {
    if (this.phase !== 'submitting') return; // 이미 확인 중·멈춤·확정이면 그대로
    if (this.displayTimer !== null) { clearTimeout(this.displayTimer); this.displayTimer = null; }
    this.setPhase('checking');
    this.openWindow();
    this.schedulePoll(this.t.pollMs);
  }

  private openWindow(): void {
    if (this.windowTimer !== null) clearTimeout(this.windowTimer);
    this.windowTimer = setTimeout(() => {
      this.windowTimer = null;
      if (this.phase !== 'checking') return;
      // 7-8: 자동 재전송·자동 조회를 멈춘다. 잠금 유지. 진행 중 요청의 응답은 계속 처리한다.
      if (this.pollTimer !== null) { clearTimeout(this.pollTimer); this.pollTimer = null; }
      if (this.resendTimer !== null) { clearTimeout(this.resendTimer); this.resendTimer = null; }
      this.setPhase('stalled');
    }, this.t.windowMs);
  }

  private scheduleResend(): void {
    if (this.phase !== 'checking' || this.inflight || this.resendTimer !== null || this.pending422 !== null) return;
    const delays = this.t.resendDelaysMs;
    const delay = delays[Math.min(this.resendIdx, delays.length - 1)]!;
    this.resendIdx++;
    this.resendTimer = setTimeout(() => {
      this.resendTimer = null;
      if (this.phase === 'checking' && !this.inflight && this.pending422 === null) void this.send();
    }, delay);
  }

  private schedulePoll(delay: number): void {
    if (this.pollTimer !== null) { clearTimeout(this.pollTimer); this.pollTimer = null; }
    if (delay <= 0) { void this.poll(); return; } // 지금 바로(422 확정용 새 조회 등)
    this.pollTimer = setTimeout(() => { this.pollTimer = null; void this.poll(); }, delay);
  }

  private async poll(): Promise<void> {
    if (this.phase !== 'checking') return;
    if (this.pollInflight) return; // 동시에 하나. 진행 중 조회가 끝나면 다음 조회를 예약한다
    await this.fetchStatus();
  }

  /** 현재 상태 조회(동시에 하나만). 결과는 applyStatus로 반영하고, 확인 중이면 다음 조회를 예약한다. */
  private fetchStatus(): Promise<StatusResult | null> {
    if (this.statusPromise !== null) return this.statusPromise;
    this.pollInflight = true;
    this.pollCount++;
    const seq = ++this.statusSeq;
    this.statusPromise = (async () => {
      const r = await this.d.transport.request('GET', `/attempts/${this.d.attemptId}/items`);
      this.pollInflight = false;
      this.statusPromise = null;
      // 요청 문맥 확인: 폐기됨·세대 바뀜·다른 시도의 응답이면 버린다(조회 실패로 다룸)
      if (this.disposed || r.gen !== currentGen()) return null;
      let out: StatusResult | null = null;
      if (r.kind === 'http' && r.status === 200 && r.body['attemptId'] === this.d.attemptId) {
        const st = r.body['status'] as AttemptStatus;
        if (st === 'submitted' || st === 'void') this.knownStatus = st;
        out = { status: st, seq };
        this.applyStatus(out);
      } else if (r.kind === 'http' && r.status === 401) {
        this.toRelogin();
        return null;
      }
      // 다음 조회(DEV-I-003 항목 1):
      //  - 이 조회가 성공했지만 422를 받기 전에 시작한 것이면 422 확정에 쓸 수 없으므로 새 조회를 바로 시작한다.
      //  - 조회 실패(네트워크 오류·5xx·그 밖의 응답·다른 시도의 응답)는 설정된 조회 간격 뒤에 다시 조회한다.
      //    422 확정 대기 중이어도 실패를 즉시 반복하지 않는다. 30초 창이 끝나면(7-8) 자동 조회를 멈추므로 요청 수도 제한된다.
      if (out === null) this.pollFailCount++;
      const freshNeeded = out !== null && this.pending422 !== null && out.seq <= this.pending422.afterSeq;
      if (this.phase === 'checking') this.schedulePoll(freshNeeded ? 0 : this.t.pollMs);
      return out;
    })();
    return this.statusPromise;
  }

  private toRelogin(): void {
    if (this.isFinal()) return;
    this.clearTimers();
    this.setPhase('relogin');
    this.d.emit({ type: 'unauthorized' });
  }

  private finish(p: SubmitPhase): void {
    this.clearTimers();
    this.setPhase(p);
  }

  private clearTimers(): void {
    for (const k of ['displayTimer', 'windowTimer', 'pollTimer', 'resendTimer'] as const) {
      const v = this[k];
      if (v !== null) { clearTimeout(v); this[k] = null; }
    }
  }

  private setPhase(p: SubmitPhase): void {
    this.phase = p;
    this.d.emit({ type: 'phase', phase: p });
  }
}
